import { Layouts } from '../../../controls/layouts';
import { Buttons } from '../../../controls/buttons';
import { Checkbox } from '../../../controls/checkbox';
import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DeveloperService } from '../../services/developer.service';
import { DuplicateCheckService } from '../../../tracking/services/duplicate-check.service';

import { ConfigurationKey } from '../../models/configuration-key';
import { DocumentConfig } from '../../models/document-config';
import { ExceptionType } from '../../models/exception-type';
import { Format } from '../../models/format';
import { FormatType } from '../../models/format-type';
import { TrackingField } from '../../models/tracking-field';
import { Variant } from '../../models/variant';
import { VariantVersion } from '../../models/variant-version';
import { ConfigurationKeyUsage } from '../../models/configuration-key-usage';
import { DocumentTypeEntity } from '../../../distribution/models';
import { ItineraryStep } from '../../models/itinerary';
import { ItineraryStepDocumentConfigEntity } from '../../models/itinerary-step-document-config-entity';
import { DeveloperArtifactService } from '../../services/developer-artifact.service';
import { AssemblyArtifact, XmlArtifact } from '../../models/artifact';
import { TransportCategory, TransportType } from '../../../transport/models/transport-type';
import { TransportService } from '../../../transport/services/transport.service';
import { FileNamingType } from '../../models/filenamingtype';
import { InitStep } from '../../../distribution/models/init-step';
import { XmlPropertyPromotionFilter } from '../../models/xml-property-promotion-filter';
import { DuplicateCheckTypeEntity } from '../../../tracking/models/duplicate-check-type';
import { ContentTypeGroup } from '../../../distribution/models/content-type-group';
import { DocumentTypeClassifierResolverEntity } from '../../models/document-type-classifier-resolver';
import { DocumentTypeClassifier } from '../../models/document-type-classifier';
import { ContentTypeService } from '../../../distribution/services/distribution-content-type.service';
import { BatchingConfiguration } from '../../models/batchconfiguration';
import { ScheduleItem } from '../../../shared/scheduling/models';
import { Trigger } from '../../../shared/scheduling/models';
import { DocumentConfigOutBatcher } from '../../models/document-config-out-batcher';
import { IdentificationType, IdentificationTypeQualifier } from '../../../partner/models';
import { PartnerIdentificationTypeService } from '../../../partner/services/partner-identification-type.service';
import { ConcurrencyKey } from '../../../configuration/models/concurrency-key';
import { ConcurrencyKeysService } from 'src/app/configuration/services/concurrency-keys.service';
import { ConfigurationService } from 'src/app/configuration/services/configuration.service';
import { CurrentUserService } from 'src/app/user/services';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { FormsModule } from '@angular/forms';
import { DeveloperExportDbviewComponent } from '../developer-export-dbview/developer-export-dbview.component';
import { DeveloperExportSourceComponent } from '../developer-export-source/developer-export-source.component';

@Component({
    selector: 'app-developer-export',
    templateUrl: './developer-export.component.html',
    styleUrls: ['./developer-export.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, Layouts, Buttons, PermissionRequiredDirective, Checkbox, FormsModule, DeveloperExportDbviewComponent, DeveloperExportSourceComponent]
})
export class DeveloperExportComponent implements OnInit, AfterViewInit {
    formatTypes: FormatType[] = [];
    formats: Format[] = [];
    variants: Variant[] = [];
    versions: VariantVersion[] = [];
    distros: DocumentTypeEntity[] = [];
    docConfigs: DocumentConfig[] = [];
    trackingFields: TrackingField[] = [];
    configKeys: ConfigurationKey[] = [];
    itinerarySteps: ItineraryStep[] = [];
    initializeSteps: InitStep[] = [];
    exceptions: ExceptionType[] = [];
    configKeyUsage: ConfigurationKeyUsage[] = [];
    transportTypes: TransportType[] = [];
    fileNamingTypes: FileNamingType[] = [];
    xmlArtifacts: XmlArtifact[] = [];
    assemblyArtifacts: AssemblyArtifact[] = [];
    documentConfigItinerarySteps: ItineraryStepDocumentConfigEntity[] = [];
    promotionFilters: XmlPropertyPromotionFilter[] = [];
    duplicateChecks: DuplicateCheckTypeEntity[] = [];
    contentTypeGroups: ContentTypeGroup[] = [];
    documentTypeClassifierResolvers: DocumentTypeClassifierResolverEntity[] = [];
    documentTypeClassifiers: DocumentTypeClassifier[] = [];
    batchingConfigurations: BatchingConfiguration[] = [];
    scheduleItems: ScheduleItem[] = [];
    scheduleTriggers: Trigger[] = [];
    documentConfigOutBatchers: DocumentConfigOutBatcher[] = [];
    identificationTypes: IdentificationType[] = [];
    identificationTypeQualifiers: IdentificationTypeQualifier[] = [];
    concurrencyKeys: ConcurrencyKey[] = [];

    includeArtifacts: boolean = true;
    defaultIncludeArtifacts: boolean = true;
    hasIncludeArtifactsAdminPermission: boolean = false;
    hasExportArtifactsPermission: boolean = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private transportService: TransportService,
        private developerArtifactService: DeveloperArtifactService,
        private duplicateCheckService: DuplicateCheckService,
        private contentTypeService: ContentTypeService,
        private partnerIdentificationService: PartnerIdentificationTypeService,
        private concurrencyKeysService: ConcurrencyKeysService,
        private configurationService: ConfigurationService,
        private currentUserService: CurrentUserService) { }

    ngAfterViewInit(): void {
        void this.refresh();
    }

    ngOnInit(): void {
        void this.loadPermissions();
    }

    async loadPermissions(): Promise<void> {
        this.hasIncludeArtifactsAdminPermission = this.currentUserService.hasPermission('ImportExport_Action_Settings_Modify');
        this.hasExportArtifactsPermission = this.currentUserService.hasPermission('ImportExport_Action_ExportArtifacts');

        this.defaultIncludeArtifacts = await this.configurationService.getEffectiveSettingBoolean('IncludeArtifactsInExport');
        this.includeArtifacts = this.defaultIncludeArtifacts && this.hasExportArtifactsPermission;
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            [   this.docConfigs,
                this.distros,
                this.formatTypes,
                this.formats,
                this.variants,
                this.versions,
                this.trackingFields,
                this.configKeys,
                this.itinerarySteps,
                this.initializeSteps,
                this.exceptions,
                this.configKeyUsage,
                this.transportTypes,
                this.fileNamingTypes,
                this.xmlArtifacts,
                this.assemblyArtifacts,
                this.documentConfigItinerarySteps,
                this.promotionFilters,
                this.duplicateChecks,
                this.contentTypeGroups,
                this.documentTypeClassifierResolvers,
                this.documentTypeClassifiers,
                this.batchingConfigurations,
                this.scheduleItems,
                this.scheduleTriggers,
                this.documentConfigOutBatchers,
                this.identificationTypes,
                this.identificationTypeQualifiers,
                this.concurrencyKeys
            ] = await Promise.all([
                this.developerService.getDocumentConfigs(),
                this.developerService.getDocumentTypes(),
                this.developerService.getFormatTypes(),
                this.developerService.getFormats(),
                this.developerService.getVariants(),
                this.developerService.getVariantVersions(),
                this.developerService.getTrackingFields(),
                this.developerService.getConfigurationKeys(),
                this.developerService.getItinerarySteps(),
                this.developerService.getInitializeSteps(),
                this.developerService.getExceptionTypes().then(e => e.filter(ex => !ex.ErrorCode.startsWith('S'))),
                this.developerService.getConfigurationKeyUsages(),
                this.transportService.getTransportTypes([TransportCategory.Outbox, TransportCategory.Polling, TransportCategory.Send]),
                this.developerService.getFileNamingTypes().then(fnt => fnt.filter(e => !e.DistributionObjectId)),
                this.developerArtifactService.getXmlArtifacts(false),
                this.developerArtifactService.getAssemblyArtifacts(false),
                this.developerService.getAllDocumentConfigItinerarySteps(),
                this.developerService.getXmlPropertyPromotionFilters(),
                this.duplicateCheckService.getDuplicateCheckTypes(),
                this.contentTypeService.getContentTypeGroups(),
                this.developerService.getDocumentTypeClassifierResolvers(),
                this.developerService.getDocumentTypeClassifiers(),
                this.developerService.getBatchingConfigurations(),
                this.developerService.getScheduleItems(),
                this.developerService.getScheduleTriggers(),
                this.developerService.getAllDocumentConfigOutBatchers(),
                this.partnerIdentificationService.getIdentificationTypes(true),
                this.partnerIdentificationService.getIdentificationTypeQualifiers(),
                this.concurrencyKeysService.getConcurrencyKeys()
            ]);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }
}
